/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import ChatbotInput from 'components/atoms/chatbot_input';
import iconIA from 'assets/images/icone-ia.svg';
import { ApplicationState } from 'store';
import { BulletinsState, IaChatMessageTypes } from 'store/modules/bulletins_store/types';
import { sendIaMessage, clearIaMessages } from 'store/modules/bulletins_store/actions';
import { SpeechButton } from 'components/atoms/speech';
import { Container } from './styles';

interface BulletinIaChatProps {
  show: boolean;
  onHide: () => void;
  bulletinFilterId: number;
  editingFilterId?: number | null;
  autoSendSave?: boolean;
  onAutoSendConsumed?: () => void;
  onResetEditing?: () => void;
  onApplyFilters?: () => void;
  onSaveFilter?: (filters: any, name: string, chatHistory: IaChatMessageTypes[]) => void;
}

const BulletinIaChat = ({
  show,
  onHide,
  bulletinFilterId,
  editingFilterId,
  autoSendSave,
  onAutoSendConsumed,
  onResetEditing,
  onApplyFilters,
  onSaveFilter,
}: BulletinIaChatProps) => {
  const dispatch = useDispatch();
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const chatRef = useRef<HTMLDivElement>(null);
  const [filterSaved, setFilterSaved] = useState(false);

  const { bulletins, request } = useSelector<ApplicationState, BulletinsState>(
    (state) => state.session.bulletins,
  );

  const messages = bulletins?.ia_chat_messages || [];
  const isLoading = request?.ia_chat?.loading || false;
  const toolAction = bulletins?.ia_chat_tool_action;
  const isEditing = !!editingFilterId && editingFilterId > 0;

  useEffect(() => {
    if (!chatRef.current) return;
    const lastChild = chatRef.current.lastChild as HTMLElement;
    lastChild?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = () => {
    const content = componentRef.current?.innerText?.trim();
    if (!content || isLoading) return;

    dispatch(sendIaMessage(content, bulletinFilterId, messages));

    if (componentRef.current) {
      componentRef.current.innerText = '';
    }
  };

  const handleApplyClick = () => {
    if (isLoading) return;
    dispatch(sendIaMessage('Aplicar filtros e buscar', bulletinFilterId, messages));
  };

  const handleSaveClick = () => {
    if (isLoading) return;
    dispatch(sendIaMessage('Salvar filtro', bulletinFilterId, messages));
  };

  const handleClearChat = () => {
    dispatch(clearIaMessages());
    setFilterSaved(false);
    onResetEditing?.();
  };

  const handleHide = () => {
    handleClearChat();
    onHide();
  };

  useEffect(() => {
    if (!toolAction?.filters) return;

    if (toolAction.tool === 'search_boletim' && onApplyFilters) {
      onApplyFilters();
      return;
    }

    if (toolAction.tool === 'save_boletim' && onSaveFilter) {
      const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
      const filterName = toolAction.name || lastUserMsg?.content || '';
      onSaveFilter(toolAction.filters, filterName, messages);
      setFilterSaved(true);
    }
  }, [toolAction]);

  useEffect(() => {
    if (!show || !autoSendSave || isLoading || messages.length === 0) return;
    dispatch(sendIaMessage('Salvar filtro', bulletinFilterId, messages));
    onAutoSendConsumed?.();
  }, [show, autoSendSave, messages.length]);

  const visibleMessages = toolAction?.tool === 'search_boletim' ? messages.slice(0, -1) : messages;

  return (
    <Modal size="lg" show={show} onHide={handleHide}>
      <Container>
        <div className="modal-header dark-bg">
          <div className="modal-title text-light d-flex w-100 align-items-center">
            <img
              alt="Ícone IA"
              src={iconIA}
              height="24"
              className="mr-2"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <h5 className="mb-0">Filtrar com IA</h5>
            <button
              aria-label="Close"
              className="close mx-0 ml-auto text-light"
              type="button"
              onClick={handleHide}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
        <div className="modal-body p-0 chatbot">
          <div className="chatbot-body">
            <div className="chatbot-messages" ref={chatRef}>
              {visibleMessages.map((msg) => (
                <div key={`${msg.role}-${msg?.content?.slice(0, 20)}`} className={msg?.role}>
                  <div className="message-box">
                    {msg?.role === 'assistant' && (
                      <div className="d-flex flex-column align-items-center">
                        <div className="ia text-white rounded">IA</div>
                        <SpeechButton
                          text={msg?.content}
                          variant="chat"
                          idleIcon="volume_up"
                          className="speech-chat-audio-outline mt-2"
                        />
                      </div>
                    )}
                    <div
                      className="message-text"
                      dangerouslySetInnerHTML={{ __html: msg?.content }}
                    />
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="assistant">
                  <div className="message-box">
                    <div className="ia text-white rounded">IA</div>
                    <div className="message-text" style={{ opacity: 0.5 }}>
                      Digitando...
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {visibleMessages.length > 1 && (
            <div className="chat-actions">
              {filterSaved ? (
                <>
                  <button type="button" className="btn-action btn-clear" onClick={handleClearChat}>
                    Criar novo filtro
                  </button>
                  <button
                    type="button"
                    className="btn-action btn-apply"
                    onClick={handleApplyClick}
                    disabled={isLoading}
                  >
                    Aplicar filtro
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn-action btn-clear" onClick={handleClearChat}>
                    Limpar filtro
                  </button>
                  <button
                    type="button"
                    className="btn-action btn-save"
                    onClick={handleSaveClick}
                    disabled={isLoading}
                  >
                    {isEditing ? 'Atualizar filtro' : 'Salvar filtro'}
                  </button>
                  <button
                    type="button"
                    className="btn-action btn-apply"
                    onClick={handleApplyClick}
                    disabled={isLoading}
                  >
                    Aplicar filtro
                  </button>
                </>
              )}
            </div>
          )}
          <ChatbotInput
            componentRef={componentRef}
            isLoading={isLoading}
            sendMessage={sendMessage}
            source="filtrar-com-ia-boletim"
          />
        </div>
      </Container>
    </Modal>
  );
};

export default BulletinIaChat;
